/**
 * LoginPage Component
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLogin } from '../hooks/useLogin';
import { LoginType } from '../types/login';
import {Login} from "@mui/icons-material";

const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const [userId, setUserId] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [rememberLogin, setRememberLogin] = useState(false);
    const { handleLogin, isLoading, error, loginType, setLoginType, lockStatus } = useLogin();

    const lockMin = Math.floor(lockStatus.lockRemainingSeconds / 60);
    const lockSec = lockStatus.lockRemainingSeconds % 60;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        await handleLogin(userId, password, undefined, rememberLogin);
    };

    const handleFindId = () => {
        navigate('/find-account?type=id');
    };

    const handleFindPassword = () => {
        navigate('/find-account?type=password');
    };

    const handleRegister = () => {
        navigate('/registration-request');
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleLoginTypeChange = (type: LoginType) => {
        setLoginType(type);
        // 타입 변경 시 입력값 초기화 (선택사항)
        setUserId('');
        setPassword('');
    };

    return (
        <div id="login-screen" className="container screen active">
            <div className="login-wrapper">
                <div className="login-header">
                    <h2 className="login-title">로그인</h2>
                    <p className="login-subtitle">계정에 로그인하여 시스템을 이용하세요</p>
                </div>

                {/* 로그인 타입 선택 탭 */}
                <div className="login-type-tabs">
                    <div className="login-type-slider" data-active={loginType}></div>
                    <button
                        type="button"
                        className={`login-type-tab ${loginType === 'rider' ? 'active' : ''}`}
                        onClick={() => handleLoginTypeChange('rider')}
                    >
                        <i className="fas fa-motorcycle"></i>
                        <span>라이더</span>
                    </button>
                    <button
                        type="button"
                        className={`login-type-tab ${loginType === 'supplier' ? 'active' : ''}`}
                        onClick={() => handleLoginTypeChange('supplier')}
                    >
                        <i className="fas fa-building"></i>
                        <span>협력사</span>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="login-form">
                    <div className="form-field">
                        <label className="field-label">
                            {loginType === 'rider' ? '아이디' : '협력사 ID'}
                        </label>
                        <div className="input-with-icon">
                            <i className="fas fa-user input-icon"></i>
                            <input
                                type="text"
                                value={userId}
                                onChange={(e) => setUserId(e.target.value.trim())}
                                placeholder={loginType === 'rider' ? '아이디' : '협력사 ID'}
                                required
                                disabled={isLoading || lockStatus.isLocked}
                            />
                        </div>
                    </div>
                    <div className="form-field">
                        <label className="field-label">비밀번호</label>
                        <div className="input-with-icon">
                            <i className="fas fa-lock input-icon"></i>
                            <input
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="비밀번호"
                                required
                                disabled={isLoading || lockStatus.isLocked}
                            />
                            <button 
                                type="button" 
                                className="password-toggle-btn"
                                onClick={togglePasswordVisibility}
                            >
                                <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                            </button>
                        </div>
                    </div>
                    
                    <div className="login-options">
                        <label className="checkbox-container">
                            <input 
                                type="checkbox" 
                                checked={rememberLogin}
                                onChange={(e) => setRememberLogin(e.target.checked)}
                            />
                            <span className="checkmark"></span>
                            로그인 상태 유지
                        </label>
                    </div>

                    {lockStatus.isLocked ? (
                        <div className="error-message" style={{ color: 'red', marginBottom: '10px', textAlign: 'center' }}>
                            비밀번호를 {lockStatus.maxAttempts}번 잘못 입력하셨습니다.<br />
                            <strong>{lockMin > 0 ? `${lockMin}분 ` : ''}{lockSec}초</strong> 후 다시 시도해주세요.
                        </div>
                    ) : !lockStatus.isLocked && lockStatus.attemptsRemaining <= 2 && lockStatus.attemptsRemaining > 0 ? (
                        <div className="error-message" style={{ color: 'red', marginBottom: '10px', textAlign: 'center' }}>
                            {error && <>{error}<br /></>}
                            비밀번호를 {lockStatus.maxAttempts - lockStatus.attemptsRemaining}번 잘못 입력하셨습니다.
                            <br />
                            {lockStatus.attemptsRemaining}번 더 실패하면 로그인이 잠깁니다.
                        </div>
                    ) : error ? (
                        <div className="error-message" style={{ color: 'red', marginBottom: '10px', textAlign: 'center' }}>
                            {error}
                        </div>
                    ) : null}
                    <button
                        type="submit"
                        className="btn btn-primary btn-gradient"
                        disabled={isLoading || lockStatus.isLocked}
                    >
                        {isLoading ? '로그인 중...' : '로그인'}
                    </button>

                    {/* 라이더만 아이디/비밀번호 찾기 표시 */}
                    {loginType === 'rider' && (
                        <div className="login-find-links">
                            <button type="button" className="link-btn" onClick={handleFindId}>
                                아이디 찾기
                            </button>
                            <span className="divider-bar">|</span>
                            <button type="button" className="link-btn" onClick={handleFindPassword}>
                                비밀번호 변경
                            </button>
                            <span className="divider-bar">|</span>
                            <button type="button" className="link-btn" onClick={handleRegister}>
                                회원가입
                            </button>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default LoginPage;
